import { useMemo, useState } from "react";
import { EVENTS, RANKING, STATUS_META, type EventStatus } from "@/mocks/gossip";

type Filter = "all" | EventStatus;

const TABS: { key: Filter; label: string }[] = [
  { key: "all", label: "全部热瓜" },
  { key: "developing", label: "发酵中" },
  { key: "updating", label: "持续更新" },
  { key: "reversed", label: "已反转" },
  { key: "done", label: "已收尾" },
];

function loadWatched(): string[] {
  try {
    return JSON.parse(localStorage.getItem("rg_watched") || "[]") as string[];
  } catch {
    return [];
  }
}

export default function HotBoard() {
  const [filter, setFilter] = useState<Filter>("all");
  const [watched, setWatched] = useState<string[]>(loadWatched);
  const [expanded, setExpanded] = useState<string>("e1");

  const list = useMemo(() => {
    const sorted = [...EVENTS].sort((a, b) => b.heat - a.heat);
    return filter === "all" ? sorted : sorted.filter((e) => e.status === filter);
  }, [filter]);

  const toggleWatch = (id: string) => {
    setWatched((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem("rg_watched", JSON.stringify(next));
      return next;
    });
  };

  const countFor = (k: Filter) => (k === "all" ? EVENTS.length : EVENTS.filter((e) => e.status === k).length);

  return (
    <section id="hot" className="relative bg-paper-50 py-12 md:py-20 scroll-mt-24">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        {/* 面包屑 */}
        <nav className="text-xs text-ink-400 mb-4" aria-label="面包屑">
          <span>今日热瓜</span>
          <span className="mx-1.5">/</span>
          <span className="text-brand-600 font-medium">实时热瓜榜</span>
        </nav>

        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-brand-600 mb-2 kicker-line">实时更新</p>
            <h2 className="font-heading font-black text-3xl md:text-4xl text-ink-900">实时热瓜榜</h2>
            <p className="mt-2 text-sm text-ink-500">
              想知道哪个瓜现在走到哪一步？顺着每张瓜卡的关键节点读，更新到最新进度。已追 <b className="text-brand-600">{watched.length}</b> 条。
            </p>
          </div>
          <div className="flex flex-wrap gap-1 bg-paper-100 border border-ink-200/70 rounded-full p-1 w-fit">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setFilter(t.key)}
                className={`px-4 h-9 rounded-full text-sm whitespace-nowrap transition-colors ${
                  filter === t.key
                    ? "bg-ink-900 text-paper-50 font-semibold"
                    : "text-ink-600 hover:bg-paper-200/70"
                }`}
              >
                {t.label}
                <span className={`ml-1.5 text-[11px] ${filter === t.key ? "text-amber-300" : "text-ink-400"}`}>
                  {countFor(t.key)}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* 事件流 */}
          <div className="lg:col-span-8 space-y-4">
            {list.length === 0 && (
              <p className="text-center text-ink-500 py-16">该状态下暂无热瓜，切换到其他分类看看。</p>
            )}
            {list.map((ev, i) => {
              const meta = STATUS_META[ev.status];
              const isWatched = watched.includes(ev.id);
              const isOpen = expanded === ev.id;
              return (
                <article
                  key={ev.id}
                  id={`ev-${ev.id}`}
                  className="scroll-mt-32 rounded-2xl border border-ink-200/70 bg-paper-50 overflow-hidden hover:border-brand-300 transition-colors"
                >
                  <div className="p-5 md:p-6">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-heading font-black text-2xl w-8 text-ink-200 select-none">{String(i + 1).padStart(2, "0")}</span>
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.badge}`}>
                        <i className={meta.icon} />
                        {meta.label}
                      </span>
                      <span className="text-xs text-ink-400">{ev.category}</span>
                      <span className="ml-auto flex items-center gap-1 text-xs text-ink-500 whitespace-nowrap">
                        <i className={`${meta.dot} w-1.5 h-1.5 rounded-full inline-block`} />
                        {ev.updated}
                      </span>
                    </div>

                    <h3 className="mt-3 font-heading font-bold text-lg md:text-xl text-ink-900 leading-snug">
                      {ev.title}
                    </h3>
                    <p className="mt-2 text-sm text-ink-600 leading-relaxed">{ev.summary}</p>

                    {/* 关键节点展开 */}
                    <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100 mt-4" : "grid-rows-[0fr] opacity-0"}`}>
                      <div className="overflow-hidden">
                        <div className="border-l-2 border-brand-200 pl-5 space-y-3">
                          {ev.nodes.map((n, idx) => (
                            <div key={idx} className="relative">
                              <span className="absolute -left-[26px] top-1.5 w-2.5 h-2.5 rounded-full bg-brand-500 ring-4 ring-brand-100" />
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-semibold text-ink-900">{n.time}</span>
                                <span className="text-[11px] px-2 py-0.5 rounded bg-paper-100 text-brand-600 border border-brand-100">
                                  {n.tag}
                                </span>
                              </div>
                              <p className="text-sm text-ink-600 mt-1">{n.text}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-ink-200/70 flex flex-wrap items-center gap-x-5 gap-y-3 text-xs text-ink-500">
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-eye-line text-ink-400" /> {ev.views} 浏览
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-fire-line text-ink-400" /> 热议 {ev.heat}°
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        <button
                          onClick={() => setExpanded(isOpen ? "" : ev.id)}
                          className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm transition-colors whitespace-nowrap ${
                            isOpen
                              ? "bg-paper-100 text-ink-700 border border-ink-200"
                              : "bg-ink-900 text-paper-50 hover:bg-ink-700"
                          }`}
                        >
                          <i className={isOpen ? "ri-arrow-up-s-line" : "ri-time-line"} />
                          {isOpen ? "收起关键节点" : `看关键节点 ${ev.nodes.length}`}
                        </button>
                        <button
                          onClick={() => toggleWatch(ev.id)}
                          aria-pressed={isWatched}
                          className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm transition-colors whitespace-nowrap border ${
                            isWatched
                              ? "bg-brand-500 text-paper-50 border-brand-500"
                              : "border-ink-300 text-ink-700 hover:border-brand-400 hover:text-brand-600"
                          }`}
                        >
                          <i className={isWatched ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                          {isWatched ? "正在追" : "追一下"}
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* 右侧：实时热搜 + 追更说明 */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
            <div className="rounded-2xl bg-paper-100 border border-ink-200/60 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-bold text-lg text-ink-900 flex items-center gap-2">
                  <i className="ri-fire-fill text-brand-500" />
                  实时热搜
                </h3>
                <span className="flex items-center gap-1.5 text-[11px] text-ink-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-500 pulse-dot" /> 3 分钟刷新
                </span>
              </div>
              <ol className="space-y-3">
                {RANKING.map((r, i) => (
                  <li key={i}>
                    <a href={`#ev-${EVENTS[i % EVENTS.length].id}`} className="flex items-center gap-3 group">
                      <span
                        className={`font-heading font-black text-base w-6 ${
                          i < 3 ? "text-brand-500" : "text-ink-300"
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="flex-1 text-sm text-ink-800 leading-snug group-hover:text-brand-600 line-clamp-1 transition-colors">
                        {r.title}
                      </span>
                      <span
                        className={`shrink-0 text-[11px] px-1.5 py-0.5 rounded ${
                          r.tag === "爆"
                            ? "bg-brand-500 text-paper-50"
                            : r.tag === "新"
                              ? "bg-amber-100 text-amber-600"
                              : "bg-paper-50 text-ink-500 border border-ink-200"
                        }`}
                      >
                        {r.tag}
                      </span>
                      <i
                        className={`shrink-0 ${r.up ? "ri-arrow-up-line text-brand-500" : "ri-arrow-down-line text-ink-300"}`}
                      />
                    </a>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-2xl overflow-hidden text-paper-50 bg-ink-900 p-6">
              <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold">
                <i className="ri-stack-line" />
                编辑部正在跟进
              </div>
              <p className="font-heading text-xl font-bold mt-3 leading-snug">追瓜别上头，先看证据链。</p>
              <p className="text-sm text-paper-100/70 mt-2 leading-relaxed">
                每张瓜卡的关键节点都标注时间与内容，顺着更新读，谁先到最新一步，谁就少当一回大冤种。
              </p>
              <a
                href="#faq"
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-300 hover:text-amber-100"
              >
                看热瓜问答
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}